import React, { useState, useContext, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import {
  Alert,
  Card,
  CardFooter,
  SurveyQuestion,
  FileHandle,
  Text,
  SubQuestion,
  SideDrawer,
  Modal,
  Flex,
  Button,
  Expandable,
  Checkbox,
  Color,
} from 'component-library';
import {
  AnswerType,
  AnswerValueType,
} from 'component-library/dashboard/surveyquestion/SurveyQuestion';
import { SaveAnswersResponse } from 'lib/useSurveyQuestions';
import {
  CmsQuestionData,
  MSClientResponse,
  Document,
  ProgramData,
  DocumentTypeEnum,
} from 'lib/interfaces';
import { Auth0FeatureContext } from 'components/util/Auth0Feature';
import { CompanyContext } from 'pages/CompanyRequired';
import TotalIncomeGrossReceiptsHelpDrawer from './TotalIncomeGrossReceiptsHelpDrawer';
import AttentionNeeded from '../../../../../components/icons/AttentionNeeded';
import { FileTypes, GraphCmsQuestionIdEnum, Programs } from 'lib/constants';
import {
  isFileSizeWithinLimits,
  isFileEncrypted,
  getTaxYearFromTaxQuestionId,
} from 'lib/helpers';
import { useCommonStores } from 'stores/useStores';

const useStyles = makeStyles(() => ({
  manualInputField: {
    '& > div': {
      width: '100%',
      maxWidth: '480px',
    },
  },
  uploadedDocs: {
    borderLeft: `1px solid ${Color.neutral._20}`,
    borderRight: `1px solid ${Color.neutral._20}`,
    borderTop: `1px solid ${Color.neutral._20}`,
    '&:nth-child(2)': {
      borderTop: 'none',
    },
  },
}));

const GENERIC_VISUAL_ERROR = `Something went wrong, please try again`;
const UPLOAD_FILE_SIZE_LIMIT = 1024 * 1024 * 20;

interface TaxReturnUploadQuestionProps {
  onComplete: (question: CmsQuestionData) => void;
  program: ProgramData;
  getQuestionProps: CmsQuestionData;
  setEditMode: (editMode: boolean) => void;
  saveAnswers?: () => Promise<SaveAnswersResponse>;
  documents?: Document[];
}

interface EncryptedFileProps {
  questionId: string | null;
  fileHandle: FileHandle | null;
}

const TaxReturnUploadQuestion = ({
  program,
  onComplete,
  getQuestionProps,
  setEditMode,
  saveAnswers,
  documents,
}: TaxReturnUploadQuestionProps) => {
  const classes = useStyles();
  const { client } = useContext(Auth0FeatureContext);
  const { company } = useContext(CompanyContext);
  const { companyStore } = useCommonStores();
  const [manualGrossInput, setManualGrossInput] = useState<{
    [id: number]: boolean;
  }>({});
  const [manualQreInput, setManualQreInput] = useState<{
    [id: number]: boolean;
  }>({});
  const [manualInputsExpanded, setManualInputsExpanded] = useState<{
    [id: number]: boolean;
  }>({});
  const [files, setFiles] = useState<Record<string, (FileHandle | Document)[]>>(
    {},
  );
  const [activeQuestion] = useState<CmsQuestionData>(getQuestionProps);
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const [showEncryptedModal, setShowEncryptedModal] = useState(false);
  const [currentEncryptedFile, setCurrentEncryptedFile] =
    useState<EncryptedFileProps>({ questionId: null, fileHandle: null });
  const [isConfirmedCheckbox, setIsConfirmedCheckbox] =
    useState<boolean>(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<
    Record<string, Document[]>
  >({});
  const [deleteLoading, setDeleteLoading] = useState<{
    [id: number]: boolean;
  }>({});
  const [existingTaxReturnYears, setExistingTaxReturnYears] = useState<
    number[]
  >([]);
  const [autoCollapse, setAutoCollapse] = useState<boolean>(false);
  const [answeredQuestionsByTaxYear, setAnsweredQuestionsByTaxYear] = useState<
    Record<number, boolean>
  >({});
  const [continueEnabled, setContinueEnabled] = useState<boolean>(false);
  /**
   * Filter out the tax return, gross receipt, and QRE questions by whether, or not
   * they meet the following conditions to show them:
   * 1. There is no tax return document in the Documents table with the same taxYear as the question
   * 2. The tax return year does not pre-date Company.year_founded or the earliest of its related entities' creations
   * 3. Company.financial_information.grossReceipts AND Company.financial_information.qualifiedResearchExpenses are null
   */
  const filteredSubQuestions: CmsQuestionData[] =
    activeQuestion.subQuestions
      ?.map((subQuestion) => {
        const questionTaxYear = getTaxYearFromTaxQuestionId(
          subQuestion.id,
          program.taxYear,
        );

        if (questionTaxYear) {
          // Tax return uploaded check
          const taxReturnAlreadyUploaded = documents?.some(
            (doc) =>
              doc.taxYear === questionTaxYear &&
              doc.documentType ===
                DocumentTypeEnum.FEDERAL_CORPORATE_INCOME_TAX_RETURN,
          );

          // Founding years check
          const companyYearFounded = company.yearFounded;
          const earliestSubsidiaryFounded =
            company.qualificationQuestionsByYear &&
            parseInt(
              company.qualificationQuestionsByYear[program.taxYear][
                GraphCmsQuestionIdEnum.YEAR_ACQUIRED_COMPANY_FOUNDED
              ],
              10,
            );
          const taxReturnYearEarlierThanCompanyFounding =
            companyYearFounded && questionTaxYear < companyYearFounded;
          const taxReturnYearEarlierSubsidiaryFounding =
            earliestSubsidiaryFounded
              ? questionTaxYear < earliestSubsidiaryFounded
              : true;

          const taxReturnYearEarlierThanFoundingYears =
            taxReturnYearEarlierThanCompanyFounding &&
            taxReturnYearEarlierSubsidiaryFounding;

          // Gross receipts AND QREs check
          const grExistsForQuestionTaxYear =
            company.financialInformation?.grossReceipts?.find(
              (gr) => gr.taxYear === questionTaxYear,
            );
          const qreExistsForQuestionTaxYear =
            company.financialInformation?.qualifiedResearchExpenses?.find(
              (gr) => gr.taxYear === questionTaxYear,
            );
          const hasGrossReceiptsAndQREValues =
            grExistsForQuestionTaxYear && qreExistsForQuestionTaxYear;

          // If either of these are true, omit the question
          if (
            taxReturnAlreadyUploaded ||
            taxReturnYearEarlierThanFoundingYears ||
            hasGrossReceiptsAndQREValues
          ) {
            return;
          }

          // Check for gross receipts
          const grossReceiptsQuestion = subQuestion.text
            ?.toLowerCase()
            ?.includes('gross receipts');
          if (grossReceiptsQuestion && grExistsForQuestionTaxYear) {
            return;
          }

          // Check for QREs
          const qreQuestion = subQuestion.text
            ?.toLowerCase()
            ?.includes('research');
          if (qreQuestion && qreExistsForQuestionTaxYear) {
            return;
          }

          // Build answeredQuestionsByTaxYear
          if (!answeredQuestionsByTaxYear[questionTaxYear]) {
            const hasGrossReceiptsValueSaved =
              grossReceiptsQuestion && subQuestion.answerValue;
            answeredQuestionsByTaxYear[questionTaxYear] =
              !!hasGrossReceiptsValueSaved;
          }

          return subQuestion;
        }
      })
      .filter((item): item is CmsQuestionData => item !== undefined) || [];

  const onContinue = () => {
    onComplete(activeQuestion);
    setEditMode(false);
  };

  const uploadTaxReturn = async (
    questionId: string,
    fileHandler: FileHandle,
    taxYear: number | null,
  ) => {
    // FIXME: UploadCompanyDocument has no server end point.
    // Refactor to use UploadCompanyDocuments
    const uploadCompanyDocument = companyStore.accessToken
      ? client.UploadCompanyDocumentPublic(
          companyStore.accessToken,
          fileHandler,
          fileHandler.name,
          program
            ? `${Programs[program?.name].display} ${
                program?.taxYear
              } Expense Classification Document`
            : '',
          program !== null ? String(program?.id) : undefined,
          null,
          taxYear,
          DocumentTypeEnum.FEDERAL_CORPORATE_INCOME_TAX_RETURN,
        )
      : client.UploadCompanyDocument(
          company.id,
          fileHandler,
          fileHandler.name,
          program
            ? `${Programs[program?.name].display} ${
                program?.taxYear
              } Expense Classification Document`
            : '',
          program !== null ? String(program?.id) : undefined,
          null,
          taxYear,
          DocumentTypeEnum.FEDERAL_CORPORATE_INCOME_TAX_RETURN,
        );

    const filePromise: Promise<{
      questionId: string;
      fileHandler: FileHandle;
      response: MSClientResponse<{ document: Document }>;
    }> = uploadCompanyDocument
      .then((response) => ({ questionId, fileHandler, response }))
      .catch((error) => error);

    return filePromise.then((result) => {
      let hasUploaded = true;
      let documentId;

      if (result.response.data) {
        const { document } = result.response.data;
        if (getQuestionProps?.subQuestions) {
          const question = getQuestionProps.subQuestions.find(
            (subQuestion) => subQuestion.id === questionId,
          );
          if (question) {
            if (Array.isArray(question.answerValue)) {
              question.answerValue.push(document.id.toString());
            } else {
              question.answerValue = [];
              question.answerValue.push(document.id.toString());
            }

            documentId = document.id;
          } else {
            fileHandler.setError(
              result.response.errorMsg || GENERIC_VISUAL_ERROR,
            );
            hasUploaded = false;
          }
        }
      }

      return { hasUploaded, documentId };
    });
  };

  const onFileAdded = async (
    questionId: string,
    fileHandle: FileHandle,
    bypassFileEncryptionValidation: boolean,
  ) => {
    const promise: Promise<void> = Promise.resolve();
    const fileSizeWithinLimits = isFileSizeWithinLimits(fileHandle.file);
    const fileEncrypted = await isFileEncrypted(fileHandle);

    if (!fileSizeWithinLimits) {
      return;
    }

    if (!bypassFileEncryptionValidation && fileEncrypted) {
      setCurrentEncryptedFile({ questionId, fileHandle });
      setShowEncryptedModal(true);
      return;
    } else {
      fileHandle.setError('');
    }

    const taxYear =
      getTaxYearFromTaxQuestionId(questionId, program.taxYear) || null;

    await uploadTaxReturn(questionId, fileHandle, taxYear).then((success) => {
      // store db document id to fileHandle
      fileHandle.documentId = success.documentId;
    });

    const questionFileList = files[questionId] ? [...files[questionId]] : [];
    const updatedFiles = {
      ...files,
      [questionId]: [...questionFileList, fileHandle],
    };
    setFiles(updatedFiles);

    // update answers if file added
    if (saveAnswers) {
      saveAnswers();
    }
  };

  const onFileRemoved = (questionId: string, file: FileHandle) => {
    const questionFileList = files[questionId] ? [...files[questionId]] : [];
    const fileIndex = questionFileList.findIndex(
      (f) => (f as FileHandle).vid === file.vid,
    );
    if (fileIndex >= 0) {
      questionFileList.splice(fileIndex, 1);
    }
    const updatedFiles = {
      ...files,
      [questionId]: questionFileList,
    };
    setFiles(updatedFiles);

    // remove documentId from answerValue array
    if (getQuestionProps?.subQuestions) {
      const question = getQuestionProps.subQuestions.find(
        (subQuestion) => subQuestion.id === questionId,
      );
      if (question && Array.isArray(question?.answerValue)) {
        if (file.documentId) {
          const documentId = file.documentId;
          const removeId = question.answerValue.findIndex(
            (id) => Number(id) === documentId,
          );

          if (removeId !== -1) {
            question.answerValue.splice(removeId, 1);
          }

          companyStore.accessToken
            ? client.DeleteDocumentPublic(
                companyStore.accessToken,
                file.documentId,
              )
            : client.DeleteDocument(file.documentId);
        }
      }
    }

    // update answers if file removed
    if (saveAnswers) {
      saveAnswers();
    }
  };

  const toggleManualInputState = (itemId: number) => {
    setManualGrossInput((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
    setManualQreInput((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
    setManualInputsExpanded((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  useEffect(() => {
    if (documents) {
      setExistingTaxReturnYears(
        documents
          .filter(
            (doc) =>
              doc.documentType ===
              DocumentTypeEnum.FEDERAL_CORPORATE_INCOME_TAX_RETURN,
          )
          .map((doc) => doc.taxYear ?? -1),
      );
    }
  }, [documents]);

  useEffect(() => {
    if (activeQuestion && activeQuestion.subQuestions) {
      const filterAnswerValues = activeQuestion.subQuestions.filter((s) =>
        Array.isArray(s.answerValue),
      );
      const uploadedDocs: { [key: string]: Document[] } = {};

      if (filteredSubQuestions.length === 0) {
        setIsConfirmedCheckbox(true);
        setAutoCollapse(true);
        onContinue();
      }

      if (documents) {
        filterAnswerValues.forEach((question) => {
          if (Array.isArray(question.answerValue)) {
            question.answerValue.forEach((answerId) => {
              documents.forEach((doc) => {
                const docIdString = doc.id.toString();

                if (docIdString === answerId) {
                  if (uploadedDocs[question.id]) {
                    uploadedDocs[question.id] = [
                      ...uploadedDocs[question.id],
                      doc,
                    ];
                  } else {
                    uploadedDocs[question.id] = [doc];
                  }
                }
              });
            });
          }
        });

        setUploadedDocuments(uploadedDocs);
        setFiles(uploadedDocs);
      }
    }
  }, [
    activeQuestion,
    documents,
    filteredSubQuestions.length,
    existingTaxReturnYears,
  ]);

  // this is to delete documents that were already uploaded
  // if customer had already previously visited the company details step
  const deleteDocument = async (
    docId: number,
    questionId: string,
    index: number,
  ) => {
    setDeleteLoading((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));

    const deleteDocument = companyStore.accessToken
      ? client.DeleteDocumentPublic(companyStore.accessToken, docId)
      : client.DeleteDocument(docId);

    await deleteDocument.then(() => {
      const updateDocumentList = uploadedDocuments[questionId].filter(
        (doc) => doc.id !== docId,
      );

      const updatedDocList = {
        ...uploadedDocuments,
        [questionId]: [...updateDocumentList],
      };

      setUploadedDocuments(updatedDocList);
      setDeleteLoading((prev) => ({
        ...prev,
        [index]: !prev[index],
      }));

      // remove documentId from answerValue array
      if (getQuestionProps?.subQuestions) {
        const question = getQuestionProps.subQuestions.find(
          (subQuestion) => subQuestion.id === questionId,
        );
        if (question && Array.isArray(question?.answerValue)) {
          const removeId = question.answerValue.findIndex(
            (id) => Number(id) === docId,
          );

          if (removeId !== -1) {
            question.answerValue.splice(removeId, 1);
          }
        }
      }

      // update answers if file removed
      if (saveAnswers) {
        saveAnswers();
      }
    });
  };

  // Update the required questions record such that React will re-render the component
  const updateAnsweredQuestionsByTaxYear = (year: number, value: boolean) => {
    setAnsweredQuestionsByTaxYear((prevState) => ({
      ...prevState,
      [year]: value,
    }));
  };

  // When the attestation checkbox or the required questions are updated, check if the continue button should be enabled
  useEffect(() => {
    const requiredQuestionsAnswered = Object.values(
      answeredQuestionsByTaxYear,
    ).every((value) => value === true);

    setContinueEnabled(isConfirmedCheckbox && requiredQuestionsAnswered);
  }, [isConfirmedCheckbox, answeredQuestionsByTaxYear]);

  return activeQuestion && activeQuestion.subQuestions ? (
    <>
      <Card noMargin>
        <SubQuestion
          key={activeQuestion.id}
          title={activeQuestion.text}
          subtitle={activeQuestion.subtitle}
          onHelpLinkClick={() => setIsHelpDrawerOpen(true)}
          fontSize={15}
          paddingTopBottom={16}
        />
        <Flex direction='column' padding={[24, 24, 8, 24]}>
          {filteredSubQuestions.length > 0 &&
            filteredSubQuestions.map((question, index) => {
              const onChangeHandler = (value: AnswerValueType) => {
                question.answerValue = value;
                const taxYear = getTaxYearFromTaxQuestionId(
                  question.id,
                  program.taxYear,
                );
                if (taxYear && value)
                  updateAnsweredQuestionsByTaxYear(taxYear, true);
              };

              // if manual input has value, show manual input state
              if (
                (question.answerType === 'currency' &&
                  !manualGrossInput[index]) ||
                (question.answerType === 'percentage' && !manualQreInput[index])
              ) {
                if (
                  question.answerValue !== undefined &&
                  question.answerValue !== null &&
                  question.answerValue !== ''
                ) {
                  toggleManualInputState(index);
                }
              }

              // Assumes the question is for gross receipts or QRE percentages
              const currencyOrPercentageQuestion =
                question.answerType === 'currency' ||
                question.answerType === 'percentage';

              return (
                <Flex
                  key={`${question.id}-${index}`}
                  direction='column'
                  padding={currencyOrPercentageQuestion ? [0, 0, 24, 0] : 0}
                >
                  {currencyOrPercentageQuestion ? (
                    <Card noBoxShadow noMargin key={`${question.id}-${index}`}>
                      <Expandable expand={manualInputsExpanded[index]}>
                        {manualInputsExpanded[index] ? (
                          <Flex
                            padding={[8, 24, 24, 24]}
                            alignItems='flex-end'
                            justifyContent='space-between'
                            className={classes.manualInputField}
                          >
                            <Flex gap={16} direction='column'>
                              <SurveyQuestion
                                label={question.text}
                                answerType={question.answerType as AnswerType}
                                onChange={onChangeHandler}
                                onBlur={() => saveAnswers && saveAnswers()}
                                placeholder={question.placeholder}
                                answerValue={question.answerValue}
                                withCard={false}
                                helperText={question.helperText}
                              />
                              {question.tooltip && (
                                <Alert
                                  text={question.tooltip}
                                  type='info'
                                  variant='card'
                                />
                              )}
                            </Flex>
                            {question.answerValue === undefined ? (
                              <Button
                                label='Cancel'
                                variant='inline'
                                onClick={() => toggleManualInputState(index)}
                              />
                            ) : (
                              <Button
                                label='Remove'
                                variant='inline'
                                onClick={() => {
                                  onChangeHandler('');
                                  saveAnswers && saveAnswers();
                                  toggleManualInputState(index);
                                }}
                              />
                            )}
                          </Flex>
                        ) : (
                          <></>
                        )}
                      </Expandable>
                      {!manualInputsExpanded[index] && (
                        <Alert
                          text={
                            manualInputsExpanded[index]
                              ? ''
                              : question.subtitle
                              ? `${question.subtitle} manually instead`
                              : 'You can add your gross receipts and QREs for this years manually instead'
                          }
                          type='info'
                          variant='in_card'
                          actions={{
                            onClick: () => toggleManualInputState(index),
                            text: manualInputsExpanded[index]
                              ? 'Cancel'
                              : question.subtitle ||
                                'Add gross receipts and QREs',
                          }}
                        />
                      )}
                    </Card>
                  ) : (
                    <>
                      <SurveyQuestion
                        tooltip="Upload this year's complete federal return. Ensure it matches your business type (e.g., 1120 for C Corps)."
                        answerType={question.answerType as AnswerType}
                        placeholder={question.placeholder}
                        answerValue={question.answerValue}
                        text={question.text}
                        withCard={false}
                        acceptedFiletypes={[
                          FileTypes.CSV,
                          FileTypes.PDF,
                          FileTypes.XLS,
                          FileTypes.XLSX,
                        ]}
                        maxFileSize={UPLOAD_FILE_SIZE_LIMIT}
                        onFileAdded={async (file: FileHandle) => {
                          await onFileAdded(question.id, file, false);
                          const taxYear = getTaxYearFromTaxQuestionId(
                            question.id,
                            program.taxYear,
                          );
                          if (taxYear)
                            updateAnsweredQuestionsByTaxYear(taxYear, true);
                        }}
                        onFileRemoved={(file: FileHandle) => {
                          onFileRemoved(question.id, file);
                        }}
                        onFileCancelled={(file: FileHandle) => {
                          onFileRemoved(question.id, file);
                        }}
                      />
                      {uploadedDocuments &&
                        uploadedDocuments[question.id] &&
                        uploadedDocuments[question.id].map((doc, index) => {
                          return (
                            <Flex
                              padding={24}
                              key={`${doc.id}-${index}`}
                              className={classes.uploadedDocs}
                              alignItems='center'
                              justifyContent='space-between'
                              gap={24}
                            >
                              <Flex.Cell>
                                <Text
                                  text={doc.name}
                                  color={Color.neutral._80}
                                />
                                <Text
                                  size={11}
                                  text='(Previously uploaded)'
                                  variant='italic'
                                  color={Color.neutral._60}
                                />
                              </Flex.Cell>
                              <Button
                                onClick={() =>
                                  deleteDocument(doc.id, question.id, doc.id)
                                }
                                label={
                                  <Text
                                    color={Color.semantic.$error50}
                                    size={13}
                                    variant='medium'
                                    text='Remove'
                                  />
                                }
                                variant='tertiary'
                                small
                                loading={deleteLoading[doc.id]}
                              />
                            </Flex>
                          );
                        })}
                    </>
                  )}
                </Flex>
              );
            })}
        </Flex>
        <Flex padding={[0, 80, 8, 24]} direction='column' gap={12}>
          {autoCollapse && (
            <Text size={18} status='in_process'>
              We have on record that you&apos;ve uploaded your tax returns(s)
              already or you are not required to upload any. Please click
              continue to proceed.
            </Text>
          )}
          <Checkbox
            dataTestId='confirmation-checkbox'
            onChange={() => {
              setIsConfirmedCheckbox(!isConfirmedCheckbox);
            }}
            options={[
              {
                checked: isConfirmedCheckbox,
                text: `I hereby confirm and review that I have either uploaded or entered all correct values for each tax year in gross receipts and QREs.`,
                value: 'confirmed',
              },
            ]}
          />
        </Flex>
        <CardFooter
          dataTestId={'tax-form-upload-question--continue'}
          primaryCtaLabel='Continue'
          primaryCtaDisabled={!continueEnabled}
          primaryOnClick={() => onContinue()}
        />
        <SideDrawer
          show={isHelpDrawerOpen}
          title={'Where to find this information'}
          closeToggle={() => setIsHelpDrawerOpen(false)}
          drawerActions={<></>}
          drawerContent={<TotalIncomeGrossReceiptsHelpDrawer />}
        />
      </Card>
      <Modal
        showModal={showEncryptedModal}
        closeToggle={() => setShowEncryptedModal(false)}
        maxWidth={536}
      >
        <Flex direction='column' alignItems='center' gap={16} padding={24}>
          <AttentionNeeded />
          <Text
            variant='bold'
            size={23}
            text='Does the file you uploaded have a password?'
          />
          <Text variant='regular' size={15} tag='span'>
            <ul>
              <li>
                If Yes - please remove the password protection and re-upload
              </li>
              <li>If No - you may proceed</li>
            </ul>
          </Text>
          <Flex gap={16} justifyContent='flex-end'>
            <Button
              variant='outlined'
              label='Remove Password'
              onClick={() => setShowEncryptedModal(false)}
              dataTestId={'encrypted-modal-secondary-action'}
            />
            <Button
              label={'Proceed with Upload'}
              onClick={() => {
                if (
                  currentEncryptedFile &&
                  currentEncryptedFile.questionId &&
                  currentEncryptedFile.fileHandle
                ) {
                  onFileAdded(
                    currentEncryptedFile.questionId,
                    currentEncryptedFile.fileHandle,
                    true,
                  );
                  setShowEncryptedModal(false);
                }
              }}
              dataTestId={'encryoted-modal-primary-action'}
            />
          </Flex>
        </Flex>
      </Modal>
    </>
  ) : (
    <></>
  );
};

export default TaxReturnUploadQuestion;
